import React, { useId } from 'react';
import { Tooltip } from 'react-tooltip';
import { classnames } from '@/utils/classnames/classnames';
import { HintTooltipProps } from './types';

/**
 * Hover/focus label for an icon trigger.
 *
 * Mirrors the retail design system's HintTooltip: a light bubble — white
 * background, dark text, ~4px radius, soft shadow, no arrow, ~12px text — that
 * sits just below the icon (position 'bottom', 8px offset). Built on
 * react-tooltip (the tooltip library electus already ships; CSS imported in
 * app/[locale]/layout.tsx). The `!` utilities override react-tooltip's default
 * dark theme / 90% font-size / 0.9 opacity so the electus tokens actually win.
 * Wrap an already-labelled control; `message` should match the control's
 * aria-label so sighted and assistive-tech users get the same label.
 */
const HintTooltip = ({
  children,
  message,
  position = 'bottom',
  containerClassName = '',
  tooltipClassName = '',
}: React.PropsWithChildren<HintTooltipProps>) => {
  const id = useId();

  return (
    <span
      data-testid="hint-tooltip"
      data-tooltip-id={id}
      data-tooltip-content={message}
      className={classnames('flex items-center', containerClassName)}
    >
      {children}
      <Tooltip
        id={id}
        place={position}
        offset={8}
        noArrow
        opacity={1}
        className={classnames(
          '!text-12 !rounded-[6px] !bg-white !px-2 !py-1 !leading-normal !text-gray-800 !shadow-[0_4px_8px_rgba(16,24,64,0.08)] [&_.react-tooltip-content-wrapper]:!p-0',
          tooltipClassName,
        )}
        style={{ zIndex: 10 }}
      />
    </span>
  );
};

export default HintTooltip;
